import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { searchMatch } from '@/lib/utils';
import { Search, ArrowLeftRight, Loader2 } from 'lucide-react';
import { listingsAPI } from '@/lib/api';
import { useSEO, SEO_KEYWORDS } from '@/hooks/useSEO';
import { useListingGrid } from '@/hooks/useListingGrid';
import ListingCard from '@/components/listings/ListingCard';

const STORAGE_KEY = 'infinite_scroll_trades';
const NAV_FLAG_KEY = 'nav_away_trades';

// Read saved state synchronously - only if user navigated away (not refresh)
const readSavedState = () => {
    try {
        const navigatedAway = sessionStorage.getItem(NAV_FLAG_KEY);
        sessionStorage.removeItem(NAV_FLAG_KEY);
        
        if (!navigatedAway) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            const state = JSON.parse(saved);
            if (state && state.listings?.length > 0 && Date.now() - state.timestamp < 10 * 60 * 1000) {
                return state;
            }
        }
    } catch (e) {
        // Ignore
    }
    return null;
};

const TradesPage = () => {
    // Check if coming from floating menu - clear cached state
    useEffect(() => {
        const navFlag = sessionStorage.getItem('nav_to_trades');
        if (navFlag) {
            sessionStorage.removeItem('nav_to_trades');
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(NAV_FLAG_KEY);
        }
    }, []);
    
    // Read saved state ONCE at initialization
    const savedStateRef = useRef(readSavedState());
    const initialState = savedStateRef.current;
    const wasRestored = initialState !== null;
    
    const [listings, setListings] = useState(initialState?.listings || []);
    const [loading, setLoading] = useState(!wasRestored);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 1);
    const [itemsPerPage, setItemsPerPage] = useState(24);
    const [totalItems, setTotalItems] = useState(initialState?.totalItems || 0);
    const [hasMore, setHasMore] = useState(initialState?.hasMore ?? true);
    
    // Grid settings using shared hook
    const { gridSettings, singleColumn } = useListingGrid();
    
    const loadMoreRef = useRef(null);
    const shouldFetchRef = useRef(!wasRestored);
    const scrollRestoredRef = useRef(false);

    // SEO
    useSEO({
        title: 'Schimburi - Piese care acceptă schimburi',
        description: 'Răsfoiește piesele de colecție care acceptă schimburi pe Colectarium.',
        keywords: SEO_KEYWORDS.base,
        url: 'https://colectarium.ro/trades'
    });

    // Restore scroll position after mount if we have saved state
    useEffect(() => {
        if (!wasRestored || !initialState?.scrollY || initialState.scrollY <= 0) {
            return;
        }
        
        if (scrollRestoredRef.current) {
            return;
        }
        
        const targetScroll = initialState.scrollY;
        let attempts = 0;
        const maxAttempts = 50;
        
        const attemptScroll = () => {
            attempts++;
            const docHeight = document.documentElement.scrollHeight;
            
            if (docHeight > targetScroll || attempts >= maxAttempts) {
                window.scrollTo({ top: targetScroll, behavior: 'instant' });
                
                setTimeout(() => {
                    const actualScroll = window.scrollY;
                    const diff = Math.abs(actualScroll - targetScroll);
                    
                    if (diff > 100 && attempts < maxAttempts) {
                        requestAnimationFrame(attemptScroll);
                    } else {
                        scrollRestoredRef.current = true;
                    }
                }, 100);
            } else {
                setTimeout(attemptScroll, 100);
            }
        };
        
        const timerId = setTimeout(attemptScroll, 50);
        return () => clearTimeout(timerId);
    }, [wasRestored, initialState?.scrollY, listings.length]);

    // Save state on scroll (debounced) and before navigation
    useEffect(() => {
        if (listings.length === 0) return;
        
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const state = {
                    listings,
                    currentPage,
                    hasMore,
                    totalItems,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            }, 200);
        };
        
        // Save immediately on mousedown before navigation
        const handleMouseDown = (e) => {
            const link = e.target.closest('a[href^="/listing/"], a[href^="/category/"], a[href^="/user/"]');
            if (link) {
                sessionStorage.setItem(NAV_FLAG_KEY, 'true');
                const state = {
                    listings,
                    currentPage,
                    hasMore,
                    totalItems,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
            clearTimeout(scrollTimeout);
        };
    }, [listings, currentPage, hasMore, totalItems]);

    // Fetch listings with infinite scroll
    useEffect(() => {
        // Skip fetch if we restored from saved state and it's the first page
        if (!shouldFetchRef.current && currentPage === 1) {
            return;
        }
        
        const fetchListings = async () => {
            if (currentPage > 1) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            try {
                const skip = (currentPage - 1) * itemsPerPage;
                const res = await listingsAPI.getAll({ 
                    accepts_trades: true,
                    status: 'active',
                    skip,
                    limit: itemsPerPage
                });
                const data = res.data?.listings || res.data || [];
                const total = res.data?.total || data.length;
                
                if (currentPage > 1) {
                    setListings(prev => [...prev, ...data]);
                } else {
                    setListings(data);
                }
                
                setTotalItems(total);
                setHasMore(skip + data.length < total);
                
                // Mark that we should fetch on subsequent page changes
                shouldFetchRef.current = true;
            } catch (error) {
                console.error('Error fetching listings:', error);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };
        fetchListings();
    }, [currentPage, itemsPerPage]);

    // Infinite scroll observer
    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore]);

    // Filter by search query (client-side)
    const filteredListings = searchQuery
        ? listings.filter(listing => searchMatch(listing.title, searchQuery))
        : listings;

    return (
        <div className="container mx-auto px-4 py-8" data-testid="trades-page">
            <Helmet>
                <title>Schimburi | Colectarium</title>
                <meta name="description" content="Răsfoiește piesele de colecție care acceptă schimburi pe Colectarium." />
            </Helmet>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
                        <ArrowLeftRight className="w-8 h-8 text-primary" />
                        Schimburi
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {totalItems} piese care acceptă schimburi
                    </p>
                </div>
                
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Caută în schimburi..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-full"
                    />
                </div>
            </div>

            {loading ? (
                <div 
                    className="grid gap-6 category-grid"
                    style={{
                        '--grid-cols-mobile': gridSettings.mobile,
                        '--grid-cols-tablet': gridSettings.tablet,
                        '--grid-cols-desktop': gridSettings.desktop,
                    }}
                >
                    {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-[340px] rounded-2xl" />
                    ))}
                </div>
            ) : filteredListings.length === 0 ? (
                <div className="text-center py-16">
                    <ArrowLeftRight className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                        {searchQuery ? 'Nu s-au găsit rezultate' : 'Nu există piese care acceptă schimburi'}
                    </h2>
                    <p className="text-muted-foreground">
                        {searchQuery ? 'Încearcă alte cuvinte cheie.' : 'Revino mai târziu pentru piese noi.'}
                    </p>
                </div>
            ) : (
                <>
                    <div 
                        className="grid gap-6 category-grid"
                        style={{
                            '--grid-cols-mobile': gridSettings.mobile,
                            '--grid-cols-tablet': gridSettings.tablet,
                            '--grid-cols-desktop': gridSettings.desktop,
                        }}
                    >
                        {filteredListings.map((listing) => (
                            <ListingCard 
                                key={listing.listing_id} 
                                listing={listing}
                                singleColumn={singleColumn}
                            />
                        ))}
                    </div>
                    
                    {/* Load more trigger */}
                    <div ref={loadMoreRef} className="flex justify-center py-8">
                        {loadingMore && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Se încarcă mai multe...</span>
                            </div>
                        )}
                        {!hasMore && listings.length > 0 && (
                            <p className="text-muted-foreground text-sm">
                                Ai văzut toate cele {totalItems} piese
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TradesPage;
